import { globalDesktopHeight, globalDesktopSize } from 'globalConstants';
import { CopilotBridge } from 'models/copilot/copilotBridge';
import CurrentUser from 'models/currentuser';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { IdentityProviderType } from 'models/identityProvider';
import Tenant from 'models/tenant';
import React from 'react';
import { GraphScopeId } from 'services/Auth/authConfig';
import authService from 'services/Auth/authService';
import AppError from 'utils/appError';
import { IGraphInterface } from './AppContextProvider';

export interface IAppContext {
  useDarkMode: boolean;
  setUseDarkMode: (useDarkMode: boolean) => void;
  isContentLoading: boolean;
  showContentLoader: () => void;
  hideContentLoader: () => void;
  unhandledError?: AppError;
  error?: AppError;
  setError: (error: string | unknown) => void;
  showNotification: (
    msg: string | undefined,
    isError?: boolean,
    isWarning?: boolean,
    isBottom?: boolean,
    // Optional action link rendered after the message (e.g. a deeplink). onClick handles navigation so the
    // caller keeps control of where it goes.
    link?: { text: string; onClick: () => void },
  ) => void;
  hasScopes: (scopes: string[]) => Promise<boolean>;
  getAccessToken: (scopes: string[]) => Promise<string>;
  setUserProfile: () => Promise<void>;
  //whether the signed-in user has a Microsoft identity (signed in with Microsoft Entra ID); every Microsoft
  //Graph and Power BI feature gates on this. A function, because the cache snapshots the context at login.
  hasMicrosoftIdentity: () => boolean;
  //starts a sign-in with the given identity provider
  //directoryId pins the directory: a Workspace domain for Google, an issuer URL for the organization sign-in
  loginWithProvider: (type: IdentityProviderType, directoryId?: string) => Promise<void>;
  isAuthenticated: boolean;
  isAuthInProgress: boolean;
  user: CurrentUser;
  showResourcePanel: boolean;
  setShowResourcePanel: (show: boolean) => void;
  toggleResourcePanel: () => void;
  toggleSideBarPanel: () => void;
  isOpenSideBarPanel: boolean;
  refreshTenantList: () => Promise<void>;
  isMainNavCollapsed: boolean;
  toggleMainNavCollapsed: () => void;
  globalDataCache: GlobalDataCache;
  cacheMiss: (count: number) => Promise<void>;
  getGraphInterface: (
    scopes: string[] | GraphScopeId,
    tenantId?: string,
    allowInteraction?: boolean,
  ) => Promise<IGraphInterface>;
  isMobileView: boolean;
  windowSize: number;
  windowHeight: number;
  isOpenHelpPanel: boolean;
  setHelpPanel: (isOpen: boolean) => void;
  adminConsentScope: (clientId: string, scopes: string[], redirectUrl: string, stateSuffix?: string) => void;
  startQuery: string | undefined;
  clearStartQuery: () => void;
  changeUserLanguage: (language: string) => Promise<string>;
  setLogo: (logo: Blob | undefined) => void;
  globalFilter: string[];
  setGlobalFilter: (filter: string[]) => void;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  switchOrg: (tenant: Tenant) => Promise<void>;
  switchOrgUnit: (tenant: Tenant) => Promise<void>;
  switchCustomer: (customerTenantId: string) => Promise<boolean>;
  clearCustomer: () => Promise<void>;
  canScopeToCustomer: () => boolean;
  isCustomerScopeActive: () => boolean;
  isSelectedScopeEnabled: (clientId: string) => boolean;
  copilotBridge: CopilotBridge;
  isOnboardingInProgress: boolean | undefined;
  setOnboardingInProgress: (inProgress: boolean) => void;
}

const appContext: IAppContext = {
  useDarkMode: false,
  setUseDarkMode: () => {},
  isContentLoading: false,
  showContentLoader: () => {},
  hideContentLoader: () => {},
  error: undefined,
  setError: () => {},
  showNotification: () => {},
  hasScopes: async () => false,
  getAccessToken: async () => '',
  setUserProfile: async () => {},
  hasMicrosoftIdentity: () => true,
  loginWithProvider: async () => {},
  isAuthenticated: false,
  isAuthInProgress: false,
  user: CurrentUser.getEmptyUser(),
  showResourcePanel: false,
  setShowResourcePanel: () => {},
  toggleResourcePanel: () => {},
  isMainNavCollapsed: false,
  toggleMainNavCollapsed: () => {},
  globalDataCache: new GlobalDataCache(),
  cacheMiss: async () => {},
  getGraphInterface: async () => {
    return { accessToken: '', client: authService.getGraphClient('') };
  },
  refreshTenantList: async () => {},
  windowSize: globalDesktopSize,
  isMobileView: false,
  toggleSideBarPanel: () => {},
  isOpenSideBarPanel: false,
  windowHeight: globalDesktopHeight,
  isOpenHelpPanel: false,
  setHelpPanel: () => {},
  adminConsentScope: async () => {},
  startQuery: undefined,
  clearStartQuery: () => {},
  changeUserLanguage: async () => '',
  setLogo: () => {},
  login: async () => {},
  logout: async () => {},
  switchOrg: async () => {},
  switchOrgUnit: async () => {},
  switchCustomer: async () => false,
  clearCustomer: async () => {},
  canScopeToCustomer: () => false,
  isCustomerScopeActive: () => false,
  globalFilter: [],
  setGlobalFilter: () => {},
  isSelectedScopeEnabled: () => false,
  copilotBridge: new CopilotBridge(),
  isOnboardingInProgress: undefined,
  setOnboardingInProgress: () => {},
};

const AppContext = React.createContext(appContext);
AppContext.displayName = 'Global app context';

export default AppContext;
